/**
 * @fileoverview Cold Room U-Values Simulation Module
 * 
 * This module provides an interactive educational tool for understanding thermal
 * transmittance (U-values) and building insulation performance. It demonstrates
 * how insulation thickness affects heat transfer through building elements.
 * 
 * @module CRUValues
 * @requires react
 * @requires antd
 * @requires react-router-dom
 * 
 * @description
 * The simulation consists of three main steps:
 * 1. U-Value Definition - Introduction to thermal transmittance concepts
 * 2. Interactive Calculator - Hands-on U-value calculation with adjustable parameters
 * 3. Building Comparison - Modern vs. old building performance analysis
 * 
 * @author HeatCraftHP Team
 * @version 1.0.0
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Button, Divider, Input, Space, Tooltip } from 'antd';
import { RedoOutlined, FilePdfOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

// Shared components
import {
  HPTitle,
  NamedItemComponent,
  RoomDiagram,
  WindowRect,
  MrHP,
  QoutationElement,
  CourseNavigationModal,
} from '../../../Shared';

// Other shared components
import { LatexRenderer } from '../../../Shared/LatexRenderer';

// Constants
import {
  FIRST_STEP,
  SECOND_STEP,
  THIRD_STEP,
  ALPHA_SI,
  ALPHA_SO,
  LAMBDA_WALL,
  D_WALL,
  LAMBDA_INSULATION,
  MIN_INSULATION,
  MAX_INSULATION,
  DEFAULT_INSULATION,
  DEFAULT_ROOM_TEMP,
  OUTDOOR_TEMP,
  MAX_TEMP_DROP,
  FIRST_ROOM_INTERVAL,
  SECOND_ROOM_INTERVAL,
  MAX_COLOR_LEVEL,
  MODERN_BUILDING_U,
  OLD_BUILDING_U,
} from './Constants';

// PDF Documents
import UValuesDocument from './HP_U_Values.pdf';
import UValuesDocumentFundamentals from './HP_U_Values_Fundamentals.pdf';
import PagesWrapper from '../../../../PagesWrapper';
import { courseStructure } from '../../Intro/courseStructure';

/**
 * Resource documents configuration
 * @constant {Array<{name: string, document: string}>}
 * @description PDF resources providing additional learning materials on U-values,
 * including fundamentals and regional building regulations
 */
const RESOURCES = [
  {
    name: 'U-Values: Fundamentals',
    document: UValuesDocumentFundamentals,
  },
  {
    name: 'Relevant regulations and standards in Germany, Belgium and Netherlands',
    document: UValuesDocument,
  },
];

/**
 * Calculate overall thermal transmittance (U-value) for a wall assembly
 * 
 * Computes the U-value based on thermal resistances in series:
 * U = 1 / (R_si + R_wall + R_insulation + R_so)
 * 
 * @function calculateUValue
 * @param {number} d - Insulation thickness in millimeters
 * @param {number} [lambdaInsulation=LAMBDA_INSULATION] - Thermal conductivity of insulation material [W/(m·K)]
 * @returns {number} Overall U-value in W/(m²·K). Lower values indicate better insulation.
 * 
 * @example
 * // Calculate U-value with 100mm insulation
 * const uValue = calculateUValue(100);
 * // Returns: ~0.25 W/(m²·K)
 * 
 * @see {@link https://en.wikipedia.org/wiki/Thermal_transmittance|U-value on Wikipedia}
 */
const calculateUValue = (d, lambdaInsulation = LAMBDA_INSULATION) => {
  const R_si = 1 / ALPHA_SI;
  const R_so = 1 / ALPHA_SO;
  const R_wall = (0.001 * D_WALL) / LAMBDA_WALL;
  const R_insulation = (0.001 * d) / lambdaInsulation;
  
  return 1 / (R_si + R_so + R_wall + R_insulation);
};

/**
 * Main U-Values simulation component
 * 
 * This component orchestrates the entire U-values learning experience,
 * managing simulation state, step navigation, and animation timing.
 * 
 * @component
 * @returns {JSX.Element} Complete U-values simulation page with navigation
 * 
 * @description
 * Features:
 * - Three-step educational progression
 * - Interactive insulation thickness control
 * - Real-time U-value calculations
 * - Building comparison animations
 * - Course navigation modal integration
 * - PDF resource links
 * 
 * @example
 * // Usage in routes
 * <Route path="/az-course/u-value" element={<CRUValues />} />
 */
export function CRUValues() {
  const location = useLocation();
  
  // Simulation state
  const [simulationStep, setSimulationStep] = useState(FIRST_STEP);
  const [dInsulation, setDInsulation] = useState(DEFAULT_INSULATION);
  const [, setTroom] = useState(DEFAULT_ROOM_TEMP);
  const [firstRoomLevel, setFirstRoomLevel] = useState(0);
  const [secondRoomLevel, setSecondRoomLevel] = useState(0);
  const [showCourseNav, setShowCourseNav] = useState(false);
  const [speedMultiplier, setSpeedMultiplier] = useState(1);
  const [modernElapsedMinutes, setModernElapsedMinutes] = useState(0);
  const [oldElapsedMinutes, setOldElapsedMinutes] = useState(0);

  // Animation effects for temperature drop visualization
  useEffect(() => {
    // Real-world time estimates based on U-values (inversely proportional)
    // Modern building (U=0.5): ~10 hours to drop 20°C
    // Old building (U=2.5): ~2 hours to drop 20°C
    const modernTotalMinutes = 600; // 10 hours
    const oldTotalMinutes = 120; // 2 hours
    
    const interval1 = setInterval(() => {
      setFirstRoomLevel((prev) => {
        const newLevel = Math.min(MAX_COLOR_LEVEL, prev + 1);
        // Update elapsed time proportionally
        setModernElapsedMinutes((newLevel / MAX_COLOR_LEVEL) * modernTotalMinutes);
        return newLevel;
      });
    }, FIRST_ROOM_INTERVAL / speedMultiplier);

    const interval2 = setInterval(() => {
      setSecondRoomLevel((prev) => {
        const newLevel = Math.min(MAX_COLOR_LEVEL, prev + 1);
        // Update elapsed time proportionally
        setOldElapsedMinutes((newLevel / MAX_COLOR_LEVEL) * oldTotalMinutes);
        return newLevel;
      });
    }, SECOND_ROOM_INTERVAL / speedMultiplier);

    return () => {
      clearInterval(interval1);
      clearInterval(interval2);
    };
  }, [speedMultiplier]);

  // Navigation handlers
  const goToStep = useCallback((step) => {
    setSimulationStep(step);
  }, []);

  const resetAnimation = useCallback(() => {
    setFirstRoomLevel(0);
    setSecondRoomLevel(0);
    setModernElapsedMinutes(0);
    setOldElapsedMinutes(0);
  }, []);

  const toggleSpeed = useCallback(() => {
    setSpeedMultiplier((prev) => {
      if (prev === 1) return 2;
      if (prev === 2) return 5;
      if (prev === 5) return 10;
      if (prev === 10) return 20;
      return 1;
    });
  }, []);

  // Render current step based on simulation state
  const renderCurrentStep = () => {
    switch (simulationStep) {
      case FIRST_STEP:
        return <FirstStep onNext={() => goToStep(SECOND_STEP)} />;
      case SECOND_STEP:
        return (
          <SecondStep
            dInsulation={dInsulation}
            setDInsulation={setDInsulation}
            setTroom={setTroom}
            onPrevious={() => goToStep(FIRST_STEP)}
            onNext={() => {
              resetAnimation();
              goToStep(THIRD_STEP);
            }}
          />
        );
      case THIRD_STEP:
        return (
          <ThirdStep
            firstRoomLevel={firstRoomLevel}
            secondRoomLevel={secondRoomLevel}
            onPrevious={() => {
              resetAnimation();
              goToStep(SECOND_STEP);
            }}
            onReset={resetAnimation}
            speedMultiplier={speedMultiplier}
            onToggleSpeed={toggleSpeed}
            modernElapsedMinutes={modernElapsedMinutes}
            oldElapsedMinutes={oldElapsedMinutes}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PagesWrapper>
      <HPTitle
        name="1. Cold room"
        subname="1.2 Building U-Values"
        onTitleClick={() => setShowCourseNav(true)}
      >
        <Space direction="vertical" size="small" align="start">
          <small className="default-gray">Resources</small>
          <Space>
            {RESOURCES.map((resource) => (
              <NamedItemComponent
                key={resource.name}
                name={resource.name}
                onClick={() => window.open(resource.document)}
              >
                <FilePdfOutlined className="default-red default-x-larger hq-clickable" />
              </NamedItemComponent>
            ))}
          </Space>
        </Space>
      </HPTitle>

      <CourseNavigationModal
        open={showCourseNav}
        onClose={() => setShowCourseNav(false)}
        courseStructure={courseStructure}
        currentPath={location.pathname}
      />

      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
        <Space align="start">{renderCurrentStep()}</Space>
      </div>
    </PagesWrapper>
  );
}

// ============================================================================
// Step Components
// ============================================================================

/**
 * First step of U-values simulation
 * 
 * Introduces the concept of thermal transmittance with theoretical foundations,
 * mathematical definitions, and key terminology.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onNext - Callback to navigate to next step
 * @returns {JSX.Element} Step one content with formulas and definitions
 * 
 * @description
 * Displays:
 * - U-value definition and significance
 * - Mathematical formula with all variables explained
 * - Room diagram with annotations
 * - Interactive U-value concept visualization
 * - Heat transfer coefficient definitions (α_si, α_so)
 * - Thermal conductivity and thickness parameters
 */
function FirstStep({ onNext }) {
  return (
    <div>
      <Space align="start">
        <RoomDiagram>
          <WindowRect />
          <UValueAnnotations />
        </RoomDiagram>

        <MrHPSection poc={true} />

        <div>
          <QoutationElement
            texts={[
              "The U-value measures the overall rate of heat transfer through a building element, such as walls, windows, or roofs, indicating how well it insulates.",
              "Expressed in watts per square meter per kelvin (W/m²K), a lower U-value means better thermal resistance and less heat loss. It accounts for conduction through materials, convection at surfaces.",
            ]}
          >
            <p className="default-gray">By definition:</p>
            <LatexRenderer
              latex={
                "$$ \\dot{Q} = U \\times A \\times \\Delta T = U_{overall} \\cdot A_{total} \\cdot (T_{room} - T_{outdoor})\\quad \\text{[W]} $$"
              }
            />
            <LatexRenderer
              latex={
                "$$ U_{element} = \\large{\\frac{1}{R_{si} + \\sum \\left(R_{n} \\right) + R_{so}}} = \\large{\\frac{1}{\\frac{1}{\\alpha_{si}} + \\sum \\left( \\frac{d_n}{\\lambda_n} \\right) + \\frac{1}{\\alpha_{so}}}} \\quad \\text{[W/m$^2$K]} $$"
              }
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'start' }}>
              <p className="default-gray">Where,</p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr max-content',
                  columnGap: 16,
                  rowGap: 8,
                  alignItems: 'center',
                }}
              >
                <FormulaVariable
                  symbol="$\alpha_{si}$"
                  description="Internal convective heat transfer coefficient"
                  unit="$\frac{W}{m^{2} K}$"
                />
                <FormulaVariable
                  symbol="$\alpha_{so}$"
                  description="External surface convective heat transfer coefficient"
                  unit="$\frac{W}{m^{2} K}$"
                />
                <FormulaVariable
                  symbol="$d_n$"
                  description="Thickness of layer n"
                  unit="$m$"
                />
                <FormulaVariable
                  symbol="$\lambda_n$"
                  description="Thermal conductivity of layer n"
                  unit="$\frac{W}{m K}$"
                />
              </div>
            </div>
            <br />
            <Button size="small" type="primary" onClick={onNext}>
              Next
            </Button>
          </QoutationElement>
        </div>

        <UValueDiagram />
      </Space>
    </div>
  );
}

/**
 * Second step of U-values simulation - Interactive calculator
 * 
 * Provides hands-on experience with U-value calculations through
 * an interactive interface where users can modify insulation thickness
 * and observe real-time impacts on thermal performance.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.dInsulation - Current insulation thickness in mm
 * @param {Function} props.setDInsulation - Setter for insulation thickness
 * @param {Function} props.setTroom - Setter for room temperature
 * @param {Function} props.onPrevious - Callback to navigate to previous step
 * @param {Function} props.onNext - Callback to navigate to next step
 * @returns {JSX.Element} Interactive calculator with controls and visualizations
 * 
 * @description
 * Features:
 * - Editable physical properties table
 * - Real-time U-value calculation
 * - Heat loss reduction percentage
 * - Interactive reduction chart with multiple curves
 * - Visual insulation diagram with dynamic thickness
 * - Comparison with non-insulated baseline
 */
function SecondStep({ dInsulation, setDInsulation, setTroom, onPrevious, onNext }) {
  const uValue = calculateUValue(dInsulation);
  const uValueNoInsulation = calculateUValue(0);
  const reduction = uValue / uValueNoInsulation;
  const reductionPercent = (100 * (1 - reduction)).toFixed(1);

  const handleInsulationChange = (value) => {
    const clampedValue = Math.max(MIN_INSULATION, Math.min(value, MAX_INSULATION));
    setDInsulation(clampedValue);
    setTroom(DEFAULT_ROOM_TEMP);
  };

  return (
    <div>
      <Space align="start">
        <div>
          <QoutationElement
            texts={["Lets play a small game and calculate the U-Value of a room's wall:"]}
          >
            <small>
              You can change the thickness of the insulation wall{' '}
              <strong className="default-black">d (insulation)</strong>
            </small>
            <br />

            <PhysicalPropertiesTable
              dInsulation={dInsulation}
              onInsulationChange={handleInsulationChange}
            />

            <Divider />

            <UValueCalculationResults
              uValue={uValue}
              uValueNoInsulation={uValueNoInsulation}
              reductionPercent={reductionPercent}
              dInsulation={dInsulation}
            />

            <Divider />

            <UValueReductionChart dInsulation={dInsulation} reduction={reduction} />

            <br />
            <Space>
              <Button size="small" type="primary" onClick={onPrevious}>
                Previous
              </Button>
              <Button size="small" type="primary" onClick={onNext}>
                Next
              </Button>
            </Space>
          </QoutationElement>
        </div>

        <InsulationDiagram dInsulation={dInsulation} uValue={uValue} reduction={reduction} />
      </Space>
    </div>
  );
}

/**
 * Third step of U-values simulation - Building performance comparison
 * 
 * Demonstrates the practical impact of U-values through an animated
 * comparison of modern well-insulated buildings versus older buildings
 * with poor insulation.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.firstRoomLevel - Animation progress for modern building (0-255)
 * @param {number} props.secondRoomLevel - Animation progress for old building (0-255)
 * @param {Function} props.onPrevious - Callback to navigate to previous step
 * @param {Function} props.onReset - Callback to reset temperature drop animation
 * @returns {JSX.Element} Building comparison with animated temperature drops
 * 
 * @description
 * Shows:
 * - Modern building (U-value: 0.2 W/m²K) - slow temperature drop
 * - Old building (U-value: 1.5 W/m²K) - rapid temperature drop
 * - Real-time temperature indicators
 * - Visual representation of heat loss rate differences
 * - European building standards context (EnEV, EPBD)
 */
function ThirdStep({ firstRoomLevel, secondRoomLevel, onPrevious, onReset, speedMultiplier, onToggleSpeed, modernElapsedMinutes, oldElapsedMinutes }) {
  return (
    <div>
      <Space align="start">
        <MrHPSection lady={true} />

        <div>
          <QoutationElement
            texts={[
              "In older buildings, U-values are typically high (poor insulation) due to single-glazed windows, uninsulated walls, and outdated materials.This leads to significant heat loss and energy waste.",
              "Modern buildings use advanced insulation, double/triple glazing, and thermal breaks to achieve much lower U-values.",
              "Standards like Germany's EnEV and the EU's EPBD mandate U-values as low as 0.1–0.2 W/m²K for walls and 0.8–1.1 W/m²K for windows, significantly cutting energy use and carbon emissions.",
            ]}
          >
                <br />
                <Button size="small" type="primary" onClick={onPrevious}>
                    Previous
                </Button>
            </QoutationElement>
        </div>

        <BuildingComparison
          firstRoomLevel={firstRoomLevel}
          secondRoomLevel={secondRoomLevel}
          speedMultiplier={speedMultiplier}
          modernElapsedMinutes={modernElapsedMinutes}
          oldElapsedMinutes={oldElapsedMinutes}
        />

        <div style={{marginLeft:20}}>
          <Space direction="vertical" size="middle" align="center">
            <Tooltip title="Reset animation" color="white">
              <Button
                size="large"
                type="default"
                shape="circle"
                icon={<RedoOutlined />}
                onClick={onReset}
                aria-label="Reset animation"
              />
            </Tooltip>
            <Tooltip title={`Speed: ${speedMultiplier}x (click to cycle)`} color="white">
              <Button
                size="large"
                type={speedMultiplier > 1 ? "primary" : "default"}
                shape="circle"
                icon={<ThunderboltOutlined />}
                onClick={onToggleSpeed}
                aria-label="Toggle animation speed"
              />
            </Tooltip>
            <div style={{ textAlign: 'center' }}>
              <small className="default-gray">Speed</small>
              <br />
              <strong className="default-blue">{speedMultiplier}x</strong>
            </div>
          </Space>
        </div>
      </Space>
    </div>
  );
}

// ============================================================================
// Helper Components
// ============================================================================

/**
 * Mascot display section
 * 
 * Renders the HeatCraftHP mascot character in a consistent SVG container.
 * Provides visual continuity and engagement across simulation steps.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.lady=false] - If true, renders female variant of mascot
 * @param {boolean} [props.poc=false] - If true, renders person of color variant
 * @returns {JSX.Element} SVG container with mascot character
 * 
 * @example
 * // Default mascot
 * <MrHPSection />
 * 
 * @example
 * // Female variant
 * <MrHPSection lady={true} />
 */
function MrHPSection({ lady = false, poc = false }) {
  return (
    <div>
      <svg
        width={150}
        height={410}
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginLeft: 50 }}
      >
        <MrHP y={0} x={0} width={135} lady={lady} poc={poc} />
      </svg>
    </div>
  );
}

/**
 * Formula variable display component for grid layouts
 * 
 * Renders a single formula variable as three aligned grid cells containing
 * the mathematical symbol, description, and unit. Designed to be used within
 * a CSS Grid parent with 'max-content 1fr max-content' column template.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.symbol - LaTeX mathematical symbol (e.g., "$\\alpha_{si}$")
 * @param {string} props.description - Human-readable description of the variable
 * @param {string} props.unit - LaTeX unit expression (e.g., "$\\frac{W}{m^{2} K}$")
 * @returns {React.Fragment} Three div elements for grid cell positioning
 * 
 * @example
 * <div style={{display: 'grid', gridTemplateColumns: 'max-content 1fr max-content'}}>
 *   <FormulaVariable
 *     symbol="$\\lambda$"
 *     description="Thermal conductivity"
 *     unit="$\\frac{W}{m K}$"
 *   />
 * </div>
 */
function FormulaVariable({ symbol, description, unit }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 4 }}>
        <LatexRenderer latex={symbol} />
      </div>
      <div>
        <p className="default-gray" style={{ margin: 0, textAlign: 'left' }}>
          {description}
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 4 }}>
        <LatexRenderer latex={unit} />
      </div>
    </>
  );
}

/**
 * SVG annotations for room diagram
 * 
 * Adds educational overlays to the room diagram showing temperature gradients,
 * heat transfer coefficients, and material properties. Designed to be used as
 * children of the RoomDiagram component.
 * 
 * @component
 * @returns {React.Fragment} SVG elements for temperature lines, labels, and arrows
 * 
 * @description
 * Annotations include:
 * - Temperature gradient line through wall (T_wall,in to T_wall,out)
 * - Internal and external heat transfer coefficients (α_si, α_so)
 * - Convection flow paths with curved arrows
 * - Material property indicators (λ, d)
 * - Temperature labels for room and outdoor conditions
 * 
 * @example
 * <RoomDiagram>
 *   <WindowRect />
 *   <UValueAnnotations />
 * </RoomDiagram>
 */
function UValueAnnotations() {
  return (
    <>
      {/* Temperature gradient line */}
      <line x1={311} y1={90} x2={329} y2={220} stroke="red" strokeWidth={3} />
      <line
        x1={280}
        y1={90}
        x2={360}
        y2={90}
        stroke="red"
        strokeWidth={1}
        strokeDasharray="3"
      />
      <line
        x1={280}
        y1={220}
        x2={360}
        y2={220}
        stroke="red"
        strokeWidth={1}
        strokeDasharray="3"
      />

      {/* Temperature labels */}
      <text x={255} y={105} fill="red" fontSize="14px">
        T
      </text>
      <text x={263} y={110} fill="red" fontSize="12px">
        wall, in
      </text>
      <text x={350} y={204} fill="red" fontSize="14px">
        T
      </text>
      <text x={358} y={209} fill="red" fontSize="12px">
        wall, out
      </text>

      {/* Convection lines */}
      <line
        x1={260}
        y1={75}
        x2={360}
        y2={75}
        stroke="red"
        strokeWidth={1}
        strokeDasharray="3"
      />
      <line
        x1={280}
        y1={235}
        x2={380}
        y2={235}
        stroke="red"
        strokeWidth={1}
        strokeDasharray="3"
      />

      {/* Convection curves */}
      <path fill="none" stroke="red" strokeWidth="3" d="M 331,220 A 20,20 0 0,0 351,235" />
      <line x1={351} y1={235} x2={375} y2={235} stroke="red" strokeWidth={3} />
      <text x={350} y={252} fill="red" fontSize="14px">
        T
      </text>
      <text x={358} y={257} fill="red" fontSize="12px">
        outdoor
      </text>

      <path fill="none" stroke="red" strokeWidth="3" d="M 309,90 A 20,20 0 0,0 289,75" />
      <line x1={289} y1={75} x2={265} y2={75} stroke="red" strokeWidth={3} />
      <text x={255} y={60} fill="red" fontSize="14px">
        T
      </text>
      <text x={263} y={65} fill="red" fontSize="12px">
        room
      </text>

      {/* Heat transfer coefficients */}
      <text x={268} y={160} fill="#0B75BD" fontSize="17px">
        α
      </text>
      <text x={280} y={165} fill="#0B75BD" fontSize="12px">
        si
      </text>

      <text x={348} y={160} fill="#0B75BD" fontSize="17px">
        α
      </text>
      <text x={360} y={165} fill="#0B75BD" fontSize="12px">
        so
      </text>

      {/* Material properties */}
      <text x={315} y={55} fill="#0B75BD" fontSize="20px" fontWeight="bold">
        λ
      </text>
      <text x={315} y={25} fill="#0B75BD" fontSize="17px">
        d
      </text>

      <line x1={310} x2={310} y1={27} y2={15} strokeWidth={1} stroke="gray" />
      <line x1={330} x2={330} y1={27} y2={15} strokeWidth={1} stroke="gray" />
    </>
  );
}

/**
 * Conceptual U-value diagram
 * 
 * Displays a simplified visual representation of the U-value concept,
 * showing heat flow between two temperature nodes through a building element.
 * 
 * @component
 * @returns {JSX.Element} SVG diagram with animated heat flow
 * 
 * @description
 * Visualization features:
 * - Two temperature nodes (T_room and T_outdoor)
 * - Building element represented by connecting line
 * - Animated heat flow arrow moving from warm to cold
 * - Heat transfer formula: Q = A · U · ΔT
 * - Color-coded temperature indicators
 * 
 * The animation loop demonstrates continuous heat transfer driven by
 * temperature difference.
 */
function UValueDiagram() {
  return (
    <div>
      <svg width={300} height={300} xmlns="http://www.w3.org/2000/svg">
        <rect x={50} y={148} height={4} width={200} fill="#0B75BD" stroke="#0B75BD" strokeWidth={1} />

        <circle cx={50} cy={150} r={10} fill="#0B75BD" stroke="#0B75BD" strokeWidth={1} />
        <circle cx={250} cy={150} r={10} fill="#0B75BD" stroke="#0B75BD" strokeWidth={1} />

        <text x={125} y={130} fill="#0B75BD" fontSize="20px">
          U , A
        </text>

        <text x={40} y={125} fill="#0B75BD" fontSize="20px">
          T
        </text>
        <text x={50} y={130} fill="#0B75BD" fontSize="12px">
          room
        </text>

        <text x={240} y={125} fill="#0B75BD" fontSize="20px">
          T
        </text>
        <text x={250} y={130} fill="#0B75BD" fontSize="12px">
          outdoor
        </text>

        <text x={85} y={50} fill="#0B75BD" fontSize="20px">
          T
        </text>
        <text x={95} y={55} fill="#0B75BD" fontSize="12px">
          room
        </text>

        <text x={135} y={52} fill="#0B75BD" fontSize="18px">
          {'>'}
        </text>

        <text x={155} y={50} fill="#0B75BD" fontSize="20px">
          T
        </text>
        <text x={165} y={55} fill="#0B75BD" fontSize="12px">
          outdoor
        </text>

        <svg x={50} y={175} xmlns="http://www.w3.org/2000/svg">
          <path
            d="M 0,5 L100,5 L100,0 L110,7.5 L100,15 L100,10 L0,10 z"
            fill="red"
            stroke="red"
            strokeWidth={1}
          />

          <text x={0} y={45} fill="#0B75BD" fontSize="20px">
            Q = A • U • ΔT
          </text>
          <text x={5} y={30} fill="#0B75BD" fontSize="10px">
            •
          </text>

          <animate attributeName="x" values="50;120;" dur="8s" repeatCount="indefinite" />
        </svg>
      </svg>
    </div>
  );
}

/**
 * Physical properties input table
 * 
 * Displays a structured table of thermal properties for the wall assembly,
 * including both fixed reference values and user-editable insulation thickness.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.dInsulation - Current insulation thickness in mm
 * @param {Function} props.onInsulationChange - Callback when insulation thickness changes
 * @returns {JSX.Element} Grid-based table with property rows
 * 
 * @description
 * Table columns:
 * - Property: Mathematical symbol (LaTeX rendered)
 * - Value: Numeric value or input field
 * - Unit: Physical unit (LaTeX rendered)
 * - Reference: Data source or editability indicator
 * 
 * Properties displayed:
 * - α_si: Internal surface heat transfer coefficient (ISO 6946)
 * - α_so: External surface heat transfer coefficient (ISO 6946)
 * - λ_wall: Wall thermal conductivity
 * - d_wall: Wall thickness
 * - λ_insulation: Insulation thermal conductivity
 * - d_insulation: Insulation thickness (editable, range: 0-500mm)
 */
function PhysicalPropertiesTable({ dInsulation, onInsulationChange }) {
  return (
    <Space size="large" align="start">
      <Space size="small" direction="vertical" align="center">
        <LatexRenderer latex={"$$\\alpha_{si}$$"} />
        <p className="default-black">{ALPHA_SI}</p>
        <LatexRenderer latex={"$$\\scriptsize{\\frac{W}{m^{2} K}}$$"} />
        <small className="default-gray">ISO 6946</small>
      </Space>
      <Space size="small" direction="vertical" align="center">
        <LatexRenderer latex={"$$\\alpha_{so}$$"} />
        <p className="default-black">{ALPHA_SO}</p>
        <LatexRenderer latex={"$$\\scriptsize{\\frac{W}{m^{2} K}}$$"} />
        <small className="default-gray">ISO 6946</small>
      </Space>
      &nbsp;&nbsp;
      <Space size="small" direction="vertical" align="center">
        <LatexRenderer latex={"$$\\lambda_{wall}$$"} />
        <p className="default-black">{LAMBDA_WALL}</p>
        <LatexRenderer latex={"$$\\scriptsize{\\frac{W}{m K}}$$"} />
      </Space>
      <Space size="small" direction="vertical" align="center">
        <LatexRenderer latex={"$$d_{wall}$$"} />
        <p className="default-black">{D_WALL}</p>
        <LatexRenderer latex={"$$\\scriptsize{mm}$$"} />
      </Space>
      &nbsp;&nbsp;
      <Space size="small" direction="vertical" align="center">
        <LatexRenderer latex={"$$\\lambda_{insulation}$$"} />
        <p className="default-black">{LAMBDA_INSULATION}</p>
        <LatexRenderer latex={"$$\\scriptsize{\\frac{W}{m K}}$$"} />
      </Space>
      <Space size="small" direction="vertical" align="center">
        <LatexRenderer latex={"$$d_{insulation}$$"} />
        <Input
          type="number"
          min={MIN_INSULATION}
          max={MAX_INSULATION}
          value={dInsulation}
          onChange={(e) => onInsulationChange(Number(e.target.value))}
        />
        <LatexRenderer latex={"$$\\scriptsize{mm}$$"} />
      </Space>
    </Space>
  );
}

/**
 * U-value calculation results display
 * 
 * Shows computed thermal transmittance values and heat loss reduction percentage
 * based on current insulation configuration.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.uValue - Calculated U-value with insulation [W/(m²·K)]
 * @param {number} props.uValueNoInsulation - Baseline U-value without insulation [W/(m²·K)]
 * @param {string} props.reductionPercent - Heat loss reduction percentage (formatted string)
 * @param {number} props.dInsulation - Current insulation thickness in mm
 * @returns {JSX.Element} Formatted calculation results with formulas and values
 * 
 * @description
 * Displays:
 * - Complete thermal resistance formula (R_T calculation)
 * - U-value with current insulation
 * - Baseline U-value without insulation
 * - Percentage reduction in heat loss
 * - Contextual interpretation of results
 * 
 * All formulas are rendered using LaTeX for mathematical clarity.
 */
function UValueCalculationResults({ uValue, uValueNoInsulation, reductionPercent, dInsulation }) {
  return (
    <Space direction="vertical" size="small" align="start">
      <LatexRenderer
        latex={
          "$$U = \\frac{1}{R_{T}} \\implies R_{T} = R_{si} + R_{wall} + R_{insulation} + R_{so} = \\frac{1}{\\alpha_{si}} + \\frac{d_{wall}}{\\lambda_{wall}} + \\frac{d_{insulation}}{\\lambda_{insulation}} + \\frac{1}{\\alpha_{so}}$$"
        }
      />
      <Space size="large">
        <LatexRenderer
          latex={
            "$$U_\\text{insulation} = " + uValue.toFixed(3) + " \\quad \\small{\\text{[W/m$^2$K]}} $$"
          }
        />
        <p className="default-gray default-small">
          <span className="default-black">{dInsulation} mm </span> of insulation will lead to{' '}
          <span className="default-red">{reductionPercent}%</span>{' '}
          <span className="default-black">reduction</span> in baseline heat loss
        </p>
      </Space>
      <LatexRenderer
        latex={
          "$$U_\\text{no insulation} = " +
          uValueNoInsulation.toFixed(3) +
          " \\quad \\small{\\text{[W/m$^2$K]}} $$"
        }
      />
    </Space>
  );
}

/**
 * U-value reduction chart visualization
 * 
 * Interactive SVG chart displaying the relationship between insulation thickness
 * and heat loss reduction percentage. Shows multiple curves for different
 * insulation materials (varying thermal conductivity).
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.dInsulation - Current insulation thickness in mm
 * @param {number} props.reduction - Current reduction ratio (0-1)
 * @returns {JSX.Element} SVG chart with curves, axes, and current value indicator
 * 
 * @description
 * Chart features:
 * - X-axis: Insulation thickness (0-500mm)
 * - Y-axis: Heat loss reduction percentage (0-100%)
 * - Main curve: Reduction for current λ_insulation
 * - Background curves: Different insulation materials (higher λ values)
 * - Vertical indicator: Current thickness setting
 * - Horizontal indicator: Corresponding reduction percentage
 * - Arrow annotation: Shows effect of increasing thermal conductivity
 * 
 * Formula displayed: R = 1 - (U_insulation / U_no_insulation)
 * 
 * @example
 * <UValueReductionChart dInsulation={100} reduction={0.67} />
 * // Shows 67% reduction at 100mm thickness
 */
function UValueReductionChart({ dInsulation, reduction }) {
  return (
    <div style={{ 
      backgroundColor: 'rgba(255, 255, 255, 0.7)', 
      padding: '16px', 
      borderRadius: '8px',
      display: 'inline-block'
    }}>
      <Space align="start" size="small">
        <svg width={500} height={140} xmlns="http://www.w3.org/2000/svg">
          <text fill="#0B75BD" fontSize="12px" x={5} y={10}>
            R (%)
          </text>
        <svg x={35} y={0}>
          {/* Axes */}
          <line stroke="gray" strokeWidth={1} x1={0} x2={0} y1={0} y2={100} />
          <line stroke="gray" strokeWidth={1} x1={0} x2={500} y1={100} y2={100} />

          {/* Main curve */}
          {Array(498)
            .fill(0)
            .map((_, i) => {
              const uVal = calculateUValue(i);
              const uRef = calculateUValue(0);
              const r = uVal / uRef;
              const y = 90 * (1 - r);

              return <circle key={i} cx={2 + i} cy={100 - y} r={1} fill="#0B75BD" stroke="none" />;
            })}

          {/* Additional curves for different lambda values */}
          {Array(5)
            .fill(0)
            .map((_, li) => {
              const factor = 1 + (2 * li + 1.25 ** li) * 0.5;
              return Array(498)
                .fill(0)
                .map((_, i) => {
                  const uVal = calculateUValue(i, LAMBDA_INSULATION * factor);
                  const uRef = calculateUValue(0, LAMBDA_INSULATION * factor);
                  const r = uVal / uRef;
                  const y = 90 * (1 - r);

                  return (
                    <circle
                      key={`${li}-${i}`}
                      cx={2 + i}
                      cy={100 - y}
                      r={1}
                      fill="#0B75BD"
                      stroke="none"
                      opacity={0.2}
                    />
                  );
                });
            })}

          {/* Lambda arrow and label */}
          <path
            fill="#0B75BD"
            stroke="none"
            d="M 300,0 L300,50 L295,50 L302.5,60 L310,50  L305,50 L305,0 z"
          />
          <text x={315} y={65} fill="#0B75BD" fontSize="16px">
            λ
          </text>
          <text x={325} y={73} fill="#0B75BD" fontSize="11px">
            insulation
          </text>

          {/* Current value indicators */}
          <line
            x1={2 + dInsulation}
            x2={2 + dInsulation}
            y1={100 - 90 * (1 - reduction)}
            y2={99}
            stroke="#0B75BD"
            strokeWidth={1}
          />
          <line
            x1={0}
            x2={2 + dInsulation}
            y1={100 - 90 * (1 - reduction)}
            y2={100 - 90 * (1 - reduction)}
            stroke="#0B75BD"
            strokeWidth={1}
          />
          <text x={2} y={95 - 90 * (1 - reduction)} fill="red">
            {(100 * (1 - reduction)).toFixed(1)}%
          </text>
          <text x={2 + dInsulation} y={115} fill="#0B75BD">
            {dInsulation} mm
          </text>
        </svg>
        <text fill="#0B75BD" fontSize="12px" x={405} y={115}>
          d insulation (mm)
        </text>
      </svg>
      <LatexRenderer
        latex={"$$R = 1 - \\frac{U_\\text{insulation}}{U_\\text{no insulation}}$$"}
      />
    </Space>
    </div>
  );
}

/**
 * Wall insulation diagram with dynamic visualization
 * 
 * temperature gradients, and dynamic performance indicators.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.dInsulation - Insulation thickness in mm
 * @param {number} props.uValue - Current U-value [W/(m²·K)]
 * @param {number} props.reduction - Heat loss reduction ratio (0-1)
 * @returns {React.Fragment} Enhanced SVG diagrams with modern visual design
 */
function InsulationDiagram({ dInsulation, uValue, reduction }) {
  const wInsulation = Math.max(0.3 * dInsulation, 8); // Minimum visible width
  const wallWidth = 65; // Increased from 50
  
  // Calculate number of heat loss arrows based on U-value
  const numArrows = Math.min(Math.floor(uValue * 8), 20);
  
  // Calculate performance color based on U-value (higher U-value = worse = red)
  const performanceColor = uValue > 1.0 
    ? '#DC2626' 
    : uValue > 0.5 
    ? '#F59E0B' 
    : '#22C55E';

  return (
    <div style={{ 
      display: 'flex', 
      gap: '40px', 
      alignItems: 'flex-start',
      marginLeft: 25 
    }}>
      {/* ========== LAYER COMPOSITION DIAGRAM ========== */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <svg width={350} height={420} xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Enhanced wall pattern */}
            <pattern id="enhanced-wall-pattern" x="0" y="0" width="12" height="12" patternUnits="userSpaceOnUse">
              <rect width="12" height="12" fill="#6B7280" opacity="0.08" />
              <line x1="0" y1="0" x2="12" y2="12" stroke="#6B7280" strokeWidth="1.5" opacity="0.25" />
              <line x1="12" y1="0" x2="0" y2="12" stroke="#6B7280" strokeWidth="1" opacity="0.15" />
            </pattern>
            
            {/* Enhanced insulation pattern */}
            <pattern id="enhanced-insulation-pattern" x="0" y="0" width="10" height="10" patternUnits="userSpaceOnUse">
              <rect width="10" height="10" fill="#EF4444" opacity="0.06" />
              <line x1="0" y1="0" x2="10" y2="10" stroke="#EF4444" strokeWidth="1.5" opacity="0.35" />
              <line x1="10" y1="0" x2="0" y2="10" stroke="#EF4444" strokeWidth="1" opacity="0.2" />
              <circle cx="5" cy="5" r="1" fill="#EF4444" opacity="0.15" />
            </pattern>
            
            {/* Shadow filter */}
            <filter id="insulation-shadow">
              <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
              <feOffset dx="0" dy="2"/>
              <feComponentTransfer><feFuncA type="linear" slope="0.25"/></feComponentTransfer>
              <feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge>
            </filter>
          </defs>
          
          {/* Title */}
          <text x={10} y={20} fill="#1F2937" fontSize="14px" fontWeight="bold">
            Wall Composition
          </text>
          
          {/* Indoor label - fixed position */}
          <text x={28} y={50} fill="gray" fontSize="12px" fontWeight="600" textAnchor="middle">
            Indoor
          </text>
          
          {/* Wall layer with enhanced styling */}
          <g filter="url(#insulation-shadow)">
            <rect
              x={70}
              y={100}
              width={wallWidth}
              height={280}
              fill="url(#enhanced-wall-pattern)"
              stroke="#4B5563"
              strokeWidth={2.5}
              rx={3}
            />
          </g>
          
          {/* Wall labels */}
          <text x={70 + wallWidth / 2} y={75} fill="#1F2937" fontSize="16px" fontWeight="bold" textAnchor="middle">
            λ
          </text>
          <text x={70 + wallWidth / 2} y={86} fill="#1F2937" fontSize="10px" textAnchor="middle">
            wall
          </text>
          
          {/* Wall dimension indicator - horizontal at base */}
          <line x1={70} y1={390} x2={70 + wallWidth} y2={390} stroke="#6B7280" strokeWidth={2} />
          <line x1={70} y1={385} x2={70} y2={395} stroke="#6B7280" strokeWidth={1.5} />
          <line x1={70 + wallWidth} y1={385} x2={70 + wallWidth} y2={395} stroke="#6B7280" strokeWidth={1.5} />
          <text x={70 + wallWidth / 2} y={408} fill="#4B5563" fontSize="11px" fontWeight="600" textAnchor="middle">
            {D_WALL} mm
          </text>
          
          {/* Heat transfer coefficient - interior */}
          <text x={40} y={225} fill="#0369A1" fontSize="18px" fontWeight="600">
            α
          </text>
          <text x={50} y={232} fill="#0369A1" fontSize="11px">
            si
          </text>
          
          {/* Insulation layer with enhanced styling */}
          <g filter="url(#insulation-shadow)">
            <rect
              x={70 + wallWidth}
              y={100}
              width={wInsulation}
              height={280}
              fill="url(#enhanced-insulation-pattern)"
              stroke="#DC2626"
              strokeWidth={2.5}
              rx={3}
            />
          </g>
          
          {/* Insulation labels */}
          <text x={70 + wallWidth + wInsulation / 2} y={75} fill="#DC2626" fontSize="16px" fontWeight="bold" textAnchor="middle">
            λ
          </text>
          <text x={70 + wallWidth + wInsulation / 2} y={86} fill="#DC2626" fontSize="10px" textAnchor="middle">
            insulation
          </text>
          
          {/* Insulation dimension indicator - horizontal at base */}
          <line 
            x1={70 + wallWidth} 
            y1={390} 
            x2={70 + wallWidth + wInsulation} 
            y2={390} 
            stroke="#DC2626" 
            strokeWidth={2} 
          />
          <line x1={70 + wallWidth} y1={385} x2={70 + wallWidth} y2={395} stroke="#DC2626" strokeWidth={1.5} />
          <line x1={70 + wallWidth + wInsulation} y1={385} x2={70 + wallWidth + wInsulation} y2={395} stroke="#DC2626" strokeWidth={1.5} />
          <text 
            x={70 + wallWidth + wInsulation / 2} 
            y={408} 
            fill="#DC2626" 
            fontSize="11px" 
            fontWeight="600"
            textAnchor="middle"
          >
            {dInsulation} mm
          </text>
          
          {/* Heat transfer coefficient - exterior */}
          <text x={70 + wallWidth + wInsulation + 10} y={225} fill="#0369A1" fontSize="18px" fontWeight="600">
            α
          </text>
          <text x={70 + wallWidth + wInsulation + 22} y={230} fill="#0369A1" fontSize="11px">
            so
          </text>
          
          {/* Outdoor label - fixed to the right */}
          <text x={305} y={50} fill="gray" fontSize="12px" fontWeight="600" textAnchor="middle">
            Outdoor
          </text>
        </svg>
      </div>

      {/* ========== HEAT TRANSFER DIAGRAM ========== */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <svg width={380} height={420} xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Temperature gradient - warm to cold */}
            <linearGradient id="temp-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ff0000ff" stopOpacity="0.5" />
              <stop offset="50%" stopColor="#F59E0B" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.3" />
            </linearGradient>
            
            {/* Arrow gradient for heat flow */}
            <linearGradient id="heat-arrow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FF6347" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#FF6347" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          
          {/* Title */}
          <text x={10} y={20} fill="#1F2937" fontSize="14px" fontWeight="bold">
            Heat Transfer
          </text>
          
          {/* Indoor label - fixed position at top */}
          <text x={28} y={50} fill="gray" fontSize="12px" fontWeight="600" textAnchor="middle">
            Indoor
          </text>
          
          {/* Wall and insulation layers */}
          <g filter="url(#insulation-shadow)">
            <rect
              x={70}
              y={100}
              width={wallWidth}
              height={280}
              fill="url(#enhanced-wall-pattern)"
              stroke="#4B5563"
              strokeWidth={2.5}
              rx={3}
            />
            <rect
              x={70 + wallWidth}
              y={100}
              width={wInsulation}
              height={280}
              fill="url(#enhanced-insulation-pattern)"
              stroke="#DC2626"
              strokeWidth={2.5}
              rx={3}
            />
          </g>
          
          {/* Temperature gradient overlay */}
          <rect
            x={70}
            y={100}
            width={wallWidth + wInsulation}
            height={280}
            fill="url(#temp-gradient)"
            opacity={0.5}
          />
          
          {/* Indoor temperature badge - fixed position */}
          <g>
            <text x={25} y={230} fill="#DC2626" fontSize="16px" fontWeight="bold" textAnchor="middle">
              20
            </text>
            <text x={43} y={230} fill="#DC2626" fontSize="12px" textAnchor="middle">
              °C
            </text>
          </g>
          
          {/* Outdoor label and temperature badge - fixed to the right */}
          <text x={335} y={50} fill="gray" fontSize="12px" fontWeight="600" textAnchor="middle">
            Outdoor
          </text>
          
          <g>
            <text x={298} y={230} fill="#1D4ED8" fontSize="16px" fontWeight="bold" textAnchor="middle">
              5
            </text>
            <text x={316} y={230} fill="#1D4ED8" fontSize="12px" textAnchor="middle">
              °C
            </text>
          </g>
          
          {/* Heat loss arrows - multiple arrows like BuildingComparison */}
          <g>
            {/* Q_loss label */}
            <text x={40} y={400} fill="#FF6347" fontSize="16px" fontWeight="bold">
              Q
            </text>
            <text x={55} y={410} fill="#FF6347" fontSize="10px">
              loss
            </text>
            
            {/* Multiple heat loss arrows */}
            {Array.from({ length: numArrows }).map((_, i) => (
              <g key={`heat-arrow-${i}`}>
                <path
                  d={`M ${75 + (i % 4) * 20} ${395 + Math.floor(i / 4) * 8} L ${100 + (i % 4) * 20} ${395 + Math.floor(i / 4) * 8} L ${95 + (i % 4) * 20} ${390 + Math.floor(i / 4) * 8} M ${100 + (i % 4) * 20} ${395 + Math.floor(i / 4) * 8} L ${95 + (i % 4) * 20} ${400 + Math.floor(i / 4) * 8}`}
                  stroke="#FF6347"
                  strokeWidth={2}
                  fill="none"
                  opacity={0.7}
                  strokeLinecap="round"
                >
                  <animate 
                    attributeName="opacity" 
                    values="0.7;0.2;0.7" 
                    dur="2s" 
                    repeatCount="indefinite" 
                    begin={`${i * 0.15}s`} 
                  />
                </path>
              </g>
            ))}
          </g>
          
          {/* Performance indicator */}
          <g>
            <rect 
              x={10} 
              y={63} 
              width={185} 
              height={26} 
              rx={6} 
              fill={performanceColor} 
              opacity={0.15}
              stroke={performanceColor}
              strokeWidth={1.5}
            />
            <text 
              x={20} 
              y={80} 
              fill={performanceColor} 
              fontSize="13px" 
              fontWeight="bold"
            >
              U-value: {uValue.toFixed(3)} W/m²K
            </text>
          </g>
          
          {/* Reduction percentage badge */}
          <g>
            <rect 
              x={205} 
              y={63} 
              width={75} 
              height={26} 
              rx={6} 
              fill={performanceColor} 
              opacity={0.2}
            />
            <text 
              x={242.5} 
              y={80} 
              fill={performanceColor} 
              fontSize="15px" 
              fontWeight="bold"
              textAnchor="middle"
            >
              ↓ {(100 * (1 - reduction)).toFixed(0)}%
            </text>
          </g>
        </svg>
      </div>
    </div>
  );
}

/**
 * Building performance comparison visualization
 * 
 * Advanced animated side-by-side comparison demonstrating temperature drop rates in
 * modern well-insulated buildings versus older poorly-insulated buildings with
 * sophisticated visual effects and detailed architectural representation.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.firstRoomLevel - Modern building animation level (0-255)
 * @param {number} props.secondRoomLevel - Old building animation level (0-255)
 * @returns {JSX.Element} SVG with two building diagrams and animated temperature drops
 * 
 * @description
 * Modern building (top):
 * - U-value: 0.2 W/(m²·K) (excellent insulation)
 * - Triple-glazed windows, thick insulation layer visualization
 * - Temperature drop: Slow (25°C → ~20°C over animation)
 * - Color-coded temperature gradient fill
 * - Minimal heat loss arrows
 * 
 * Old building (bottom):
 * - U-value: 1.5 W/(m²·K) (poor insulation)
 * - Single-glazed windows, thin/no insulation
 * - Temperature drop: Fast (25°C → ~5°C over animation)
 * - Dramatic color transition showing heat loss
 * - Multiple heat loss arrows indicating poor performance
 * 
 * features:
 * - Real-time temperature display with thermometer icons
 * - Animated horizontal line showing temperature descent
 * - Gradient fills representing thermal distribution
 * - Heat loss visualization through animated arrows
 * - Window and door architectural details
 * - Insulation layer thickness indicators
 * - Outdoor temperature reference line (5°C)
 * - Professional typography and spacing
 * - Color-coded performance indicators
 * 
 * The visualization dramatically illustrates how insulation quality affects
 * building thermal performance, energy efficiency, and occupant comfort through
 * scientifically accurate yet visually engaging animations.
 */
function BuildingComparison({ firstRoomLevel, secondRoomLevel, speedMultiplier = 1, modernElapsedMinutes = 0, oldElapsedMinutes = 0 }) {
  const modernTemp = (25 - MAX_TEMP_DROP * (firstRoomLevel / MAX_COLOR_LEVEL)).toFixed(1);
  const oldTemp = (25 - MAX_TEMP_DROP * (secondRoomLevel / MAX_COLOR_LEVEL)).toFixed(1);
  
  // Format elapsed time (real-world physics-based)
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };
  
  const modernTimeDisplay = formatTime(modernElapsedMinutes);
  const oldTimeDisplay = formatTime(oldElapsedMinutes);
  
  // Calculate temperature-based colors (warm to cold)
  const modernTempRatio = firstRoomLevel / MAX_COLOR_LEVEL;
  const oldTempRatio = secondRoomLevel / MAX_COLOR_LEVEL;
  
  // Modern building: starts warm orange, transitions to comfortable yellow
  const modernColor = `rgb(${255 - modernTempRatio * 155}, ${200 - modernTempRatio * 150}, ${100 + modernTempRatio * 155})`;
  
  // Old building: starts warm orange, transitions dramatically to cold blue
  const oldColor = `rgb(${255 - oldTempRatio * 155}, ${200 - oldTempRatio * 150}, ${100 + oldTempRatio * 155})`;
  
  // Heat loss intensity (more arrows = more heat loss)
  const modernHeatLoss = Math.floor((1-modernTempRatio) * 3); // 0-3 arrows
  const oldHeatLoss = Math.floor((1-oldTempRatio) * 15); // 0-15 arrows

  return (
    <div style={{ marginLeft: 50 }}>
      <svg width={500} height={640} xmlns="http://www.w3.org/2000/svg">
        <defs>
          {/* Gradient definitions for temperature visualization */}
          <linearGradient id="modernGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={modernColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={modernColor} stopOpacity="0.15" />
          </linearGradient>
          <linearGradient id="oldGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={oldColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={oldColor} stopOpacity="0.15" />
          </linearGradient>
          
          {/* Insulation pattern */}
          <pattern id="insulationPattern" x="0" y="0" width="8" height="8" patternUnits="userSpaceOnUse">
            <rect width="8" height="8" fill="#0B75BD" opacity="0.1" />
            <line x1="0" y1="0" x2="8" y2="8" stroke="#0B75BD" strokeWidth="1" opacity="0.3" />
          </pattern>
          
          {/* Poor insulation pattern */}
          <pattern id="poorInsulationPattern" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
            <rect width="4" height="4" fill="gray" opacity="0.05" />
          </pattern>
        </defs>

        {/* ========== MODERN BUILDING ========== */}
        <g id="modernBuilding">
          {/* Title and U-value badge */}
          <rect x={15} y={5} width={140} height={24} rx={4} fill="#0B75BD" opacity={0.1} />
          <text fill="#0B75BD" fontSize="15px" fontWeight="bold" x={22} y={22}>
            Modern Building
          </text>
          
          <rect x={260} y={5} width={150} height={24} rx={4} fill="#4fb1f334" />
          <text fill="#0B75BD" fontSize="13px" fontWeight="bold" x={270} y={22}>
            U-value: {MODERN_BUILDING_U} W/m²K
          </text>
          
          {/* Elapsed time badge */}
          <Tooltip title="Real-world time elapsed based on thermal physics calculations. Modern buildings cool down much slower due to excellent insulation." placement="left" color="white">
            <g style={{ cursor: 'pointer' }}>
              {(() => {
                const badgeWidth = 75;
                const badgeX = 500 - badgeWidth - 5;
                const numberRegionWidth = 55;
                const iconX = badgeX + badgeWidth - 12;
                return (
                  <>
                    <rect x={badgeX} y={5} width={badgeWidth} height={24} rx={4} fill="white" opacity={0.95} stroke="#666" strokeWidth={0.5} />
                    <text fill="#333" fontSize="11px" fontWeight="600" x={badgeX + numberRegionWidth / 2} y={22} textAnchor="middle">
                      {modernTimeDisplay}
                    </text>
                    <circle cx={iconX} cy={17} r={6} fill="none" stroke="#666" strokeWidth={1.3} />
                    <line x1={iconX} y1={17} x2={iconX} y2={13.5} stroke="#666" strokeWidth={1.3} />
                    <line x1={iconX} y1={17} x2={iconX + 2.5} y2={17} stroke="#666" strokeWidth={1.3} />
                  </>
                );
              })()}
            </g>
          </Tooltip>

          {/* Left insulation layer visualization */}
          <rect
            x={30}
            y={45}
            width={12}
            height={210}
            fill="url(#insulationPattern)"
            stroke="#0B75BD"
            strokeWidth={2}
          />
          <text x={15} y={150} fill="#0B75BD" fontSize="13px" transform="rotate(-90 15 150)">
            Insulation
          </text>
          
          {/* Main building structure with gradient fill */}
          <rect
            x={42}
            y={45}
            width={340}
            height={210}
            fill="url(#modernGradient)"
            stroke="#0B75BD"
            strokeWidth={4}
            rx={2}
          />
          
          {/* Right insulation layer visualization */}
          <rect
            x={382}
            y={45}
            width={12}
            height={210}
            fill="url(#insulationPattern)"
            stroke="#0B75BD"
            strokeWidth={2}
          />
          
          {/* Roof detail */}
          <path
            d="M 38 45 L 212 15 L 386 45 Z"
            fill="#0B75BD"
            opacity={0.2}
            stroke="#0B75BD"
            strokeWidth={2}
          />
          
          {/* Windows (triple-glazed appearance) */}
          {[180, 280].map((x, i) => (
            <Tooltip 
              key={`modern-window-${i}`}
              title="Triple-glazed windows - Excellent insulation with three layers of glass and two air gaps. U-value ≈ 0.6 W/m²K"
              placement="top"
              color="white"
            >
              <g style={{ cursor: 'pointer' }}>
                <rect x={x} y={80} width={50} height={60} fill="#87CEEB" opacity={0.4} stroke="#0B75BD" strokeWidth={3} />
                <rect x={x + 2} y={82} width={46} height={56} fill="none" stroke="#0B75BD" strokeWidth={1} />
                <line x1={x + 25} y1={80} x2={x + 25} y2={140} stroke="#0B75BD" strokeWidth={2} />
                <line x1={x} y1={110} x2={x + 50} y2={110} stroke="#0B75BD" strokeWidth={2} />
              </g>
            </Tooltip>
          ))}
          
          {/* Door */}
          <rect x={350} y={185} width={30} height={70} fill="#8B4513" stroke="#654321" strokeWidth={2} rx={1} />
          <circle cx={375} cy={220} r={2} fill="#FFD700" />
          
          {/* Temperature indicator with thermometer icon */}
          <g id="modernThermometer">
            <rect x={45} y={50} width={28} height={90} rx={14} fill="white" stroke="#0B75BD" strokeWidth={2} />
            {(() => {
              const mercuryHeight = ((parseFloat(modernTemp) - OUTDOOR_TEMP) / (25 - OUTDOOR_TEMP)) * 70;
              return <rect x={52} y={130 - mercuryHeight} width={14} height={mercuryHeight} fill={modernColor} opacity={0.7} />;
            })()}
            <circle cx={59} cy={130} r={10} fill={modernColor} stroke="#0B75BD" strokeWidth={2} />
            <text x={80} y={90} fill="#0B75BD" fontSize="20px" fontWeight="bold">
              {modernTemp}°C
            </text>
            <text x={80} y={107} fill="#0B75BD" fontSize="11px">
              Indoor
            </text>
          </g>
          
          {/* Animated temperature descent line */}
          <line
            x1={42}
            x2={382}
            y1={65 + 180 * modernTempRatio}
            y2={65 + 180 * modernTempRatio}
            stroke="#FF6347"
            strokeWidth={2.5}
            strokeDasharray="6,3"
            opacity={0.7}
          />
          
          {/* Heat loss arrows (minimal for modern building) */}
          {Array.from({ length: modernHeatLoss }).map((_, i) => (
            <g key={`modern-heat-${i}`}>
              <path
                d={`M ${405 + i * 15} ${80 + i * 50} L ${425 + i * 15} ${80 + i * 50} L ${420 + i * 15} ${75 + i * 50} M ${425 + i * 15} ${80 + i * 50} L ${420 + i * 15} ${85 + i * 50}`}
                stroke="#FF6347"
                strokeWidth={1.5}
                fill="none"
                opacity={0.4}
              >
                <animate attributeName="opacity" values="0.4;0.1;0.4" dur="2s" repeatCount="indefinite" />
              </path>
            </g>
          ))}
          
          {/* Outdoor temperature reference */}
          <text fill="#4682B4" fontSize="14px" x={405} y={150}>
            {OUTDOOR_TEMP}°C
          </text>
          <text fill="#4682B4" fontSize="10px" x={400} y={163}>
            Outdoor
          </text>
          
          {/* Ground/foundation */}
          <line x1={0} x2={460} y1={255} y2={255} stroke="#34A56F" strokeWidth={14} />
          
          {/* Performance indicator */}
          <circle cx={450} cy={220} r={20} fill="#22C55E" opacity={0.2} stroke="#22C55E" strokeWidth={2} />
          <text x={442} y={227} fontSize="20px" fill="#22C55E" fontWeight="bold">✓</text>
        </g>

        {/* ========== SEPARATOR ========== */}
        <text x={235} y={293} fill="#0B75BD" fontSize="20px" fontWeight="bold">VS</text>

        {/* ========== OLD BUILDING ========== */}
        <g id="oldBuilding">
          {/* Title and U-value badge */}
          <rect x={15} y={315} width={120} height={24} rx={4} fill="gray" opacity={0.2} />
          <text fill="gray" fontSize="15px" fontWeight="bold" x={22} y={332}>
            Old Building
          </text>
          
          <rect x={260} y={315} width={150} height={24} rx={4} fill="#f35b5b38" />
          <text fill="red" fontSize="13px" fontWeight="bold" x={270} y={332}>
            U-value: {OLD_BUILDING_U} W/m²K
          </text>
          
          {/* Elapsed time badge */}
          <Tooltip title="Real-world time elapsed based on thermal physics calculations. Old buildings cool down rapidly due to poor insulation and air leakage." placement="left" color="white">
            <g style={{ cursor: 'pointer' }}>
              {(() => {
                const badgeWidth = 75;
                const badgeX = 500 - badgeWidth - 5;
                const numberRegionWidth = 55;
                const iconX = badgeX + badgeWidth - 12;
                return (
                  <>
                    <rect x={badgeX} y={315} width={badgeWidth} height={24} rx={4} fill="white" opacity={0.95} stroke="#666" strokeWidth={0.5} />
                    <text fill="#333" fontSize="11px" fontWeight="600" x={badgeX + numberRegionWidth / 2} y={332} textAnchor="middle">
                      {oldTimeDisplay}
                    </text>
                    <circle cx={iconX} cy={327} r={6} fill="none" stroke="#666" strokeWidth={1.3} />
                    <line x1={iconX} y1={327} x2={iconX} y2={323.5} stroke="#666" strokeWidth={1.3} />
                    <line x1={iconX} y1={327} x2={iconX + 2.5} y2={327} stroke="#666" strokeWidth={1.3} />
                  </>
                );
              })()}
            </g>
          </Tooltip>

          {/* Thin/poor insulation layer */}
          <rect
            x={30}
            y={355}
            width={5}
            height={210}
            fill="url(#poorInsulationPattern)"
            stroke="gray"
            strokeWidth={1}
            opacity={0.5}
          />
          <text x={15} y={485} fill="gray" fontSize="13px" transform="rotate(-90 15 485)">
            Poor Insulation
          </text>
          
          {/* Main building structure with gradient fill */}
          <rect
            x={35}
            y={355}
            width={340}
            height={210}
            fill="url(#oldGradient)"
            stroke="gray"
            strokeWidth={7}
          />
          
          {/* Flat/basic roof */}
          <rect x={32} y={350} width={346} height={10} fill="gray" opacity={0.4} />
          
          {/* Windows (single-glazed appearance with cracks) */}
          {[170, 270].map((x, i) => (
            <Tooltip 
              key={`old-window-${i}`}
              title="Single-glazed windows - Poor insulation with only one layer of glass. U-value ≈ 5.0 W/m²K (very high heat loss)"
              placement="top"
              color="white"
            >
              <g style={{ cursor: 'pointer' }}>
                <rect x={x} y={390} width={50} height={60} fill="#B0E0E6" opacity={0.3} stroke="gray" strokeWidth={2} />
                <line x1={x + 25} y1={390} x2={x + 25} y2={450} stroke="gray" strokeWidth={1} />
                <line x1={x} y1={420} x2={x + 50} y2={420} stroke="gray" strokeWidth={1} />
                {/* Crack indication */}
                <path d={`M ${x + 10} ${395} L ${x + 15} ${405} L ${x + 12} ${415}`} stroke="gray" strokeWidth={0.5} opacity={0.5} />
              </g>
            </Tooltip>
          ))}
          
          {/* Old door */}
          <rect x={343} y={495} width={30} height={70} fill="#654321" stroke="#4A3728" strokeWidth={2} />
          <circle cx={368} cy={530} r={2} fill="gray" />
          
          {/* Temperature indicator with thermometer icon */}
          <g id="oldThermometer">
            <rect x={38} y={360} width={28} height={90} rx={14} fill="white" stroke="gray" strokeWidth={2} />
            {(() => {
              const mercuryHeight = ((parseFloat(oldTemp) - OUTDOOR_TEMP) / (25 - OUTDOOR_TEMP)) * 70;
              return <rect x={45} y={440 - mercuryHeight} width={14} height={mercuryHeight} fill={oldColor} opacity={0.7} />;
            })()}
            <circle cx={52} cy={440} r={10} fill={oldColor} stroke="gray" strokeWidth={2} />
            <text x={75} y={400} fill="gray" fontSize="20px" fontWeight="bold">
              {oldTemp}°C
            </text>
            <text x={75} y={417} fill="gray" fontSize="11px">
              Indoor
            </text>
          </g>
          
          {/* Animated temperature descent line */}
          <line
            x1={35}
            x2={375}
            y1={375 + 180 * oldTempRatio}
            y2={375 + 180 * oldTempRatio}
            stroke="#FF6347"
            strokeWidth={3}
            strokeDasharray="6,3"
            opacity={0.8}
          />
          
          {/* Heat loss arrows (many for old building) */}
          {Array.from({ length: oldHeatLoss }).map((_, i) => (
            <g key={`old-heat-${i}`}>
              <path
                d={`M ${385 + (i % 3) * 15} ${390 + Math.floor(i / 3) * 40} L ${410 + (i % 3) * 15} ${390 + Math.floor(i / 3) * 40} L ${405 + (i % 3) * 15} ${385 + Math.floor(i / 3) * 40} M ${410 + (i % 3) * 15} ${390 + Math.floor(i / 3) * 40} L ${405 + (i % 3) * 15} ${395 + Math.floor(i / 3) * 40}`}
                stroke="#FF6347"
                strokeWidth={2}
                fill="none"
                opacity={0.7}
              >
                <animate attributeName="opacity" values="0.7;0.2;0.7" dur="1.5s" repeatCount="indefinite" begin={`${i * 0.2}s`} />
              </path>
            </g>
          ))}
          
          {/* Outdoor temperature reference */}
          <text fill="#4682B4" fontSize="14px" x={390} y={460}>
            {OUTDOOR_TEMP}°C
          </text>
          <text fill="#4682B4" fontSize="10px" x={385} y={473}>
            Outdoor
          </text>
          
          {/* Ground/foundation */}
          <line x1={0} x2={450} y1={565} y2={565} stroke="#34A56F" strokeWidth={14} />
          
          {/* Performance indicator */}
          <circle cx={440} cy={530} r={20} fill="#DC2626" opacity={0.2} stroke="#DC2626" strokeWidth={2} />
          <text x={430} y={538} fontSize="24px" fill="#DC2626" fontWeight="bold">✗</text>
        </g>

        {/* Energy efficiency comparison legend */}
        <g id="legend">
          <text x={0} y={600} fill="#0B75BD" fontSize="12px" fontWeight="bold">
            Energy Loss Comparison:
          </text>
          <rect x={0} y={607} width={78} height={16} fill="#22C55E" opacity={0.3} rx={2} />
          <text x={5} y={619} fill="#22C55E" fontSize="11px" fontWeight="bold">
            Minimal Loss
          </text>
          <rect x={95} y={607} width={70} height={16} fill="#DC2626" opacity={0.3} rx={2} />
          <text x={100} y={619} fill="#DC2626" fontSize="11px" fontWeight="bold">
            Severe Loss
          </text>
        </g>
      </svg>
    </div>
  );
}

export default CRUValues;
